import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  KanbanBoard,
  KanbanClienteBusca,
  KanbanDetalhe,
  KanbanMotivoPerda,
  KanbanNegocio,
  KanbanSeller,
  KanbanArquivo,
  KanbanConfig,
  KanbanProdutoBusca,
  KanbanDashboardReport,
  KanbanImportResult
} from '../../models/kanban/kanban';

@Injectable({
  providedIn: 'root'
})
export class KanbanService {
  private baseUrl: string;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') baseUrl: string,
    private toastr: ToastrService
  ) {
    this.baseUrl = baseUrl;
  }

  private guid(): string {
    return localStorage.getItem('guidclientenapis');
  }

  private sellerId(): number {
    return parseInt(localStorage.getItem('id'), 10);
  }

  getBoard(dataInicial?: string, dataFinal?: string): Observable<KanbanBoard> {
    return this.http.post<KanbanBoard>(this.baseUrl + 'api/Kanban/GetBoard', {
      guidclientenapis: this.guid(),
      seller_id: this.sellerId(),
      ...(dataInicial || dataFinal ? { data_inicial: dataInicial, data_final: dataFinal } : {})
    }, { headers: this.headers }).pipe(
      catchError(erro => {
        if (dataInicial || dataFinal) return throwError(() => erro);
        this.toastr.error('', 'Houve um erro ao carregar o Kanban.');
        return of({ departamentos: [], tags: [], formularios: [], formulario_campos: [], formulario_opcoes: [] });
      })
    );
  }

  getDashboardReport(formularioTipoId?: number, dataInicial?: string, dataFinal?: string): Observable<KanbanDashboardReport> {
    return this.http.post<KanbanDashboardReport>(this.baseUrl + 'api/Kanban/GetDashboardReport', {
      guidclientenapis: this.guid(),
      seller_id: this.sellerId(),
      formulario_tipo_id: formularioTipoId || null,
      ...(dataInicial || dataFinal ? { data_inicial: dataInicial, data_final: dataFinal } : {})
    }, { headers: this.headers }).pipe(
      catchError(erro => {
        if (dataInicial || dataFinal) return throwError(() => erro);
        this.toastr.error('', 'Houve um erro ao carregar o dashboard do Kanban.');
        return of({ formularios: [], campos: [], linhas: [] });
      })
    );
  }

  importDeals(etapaId: number, formularioTipoId: number, file: File): Observable<KanbanImportResult> {
    const form = new FormData();
    form.append('guidclientenapis', this.guid());
    form.append('seller_id', this.sellerId().toString());
    form.append('etapa_id', etapaId.toString());
    form.append('formulario_tipo_id', formularioTipoId.toString());
    form.append('arquivo', file, file.name);

    return this.http.post<KanbanImportResult>(this.baseUrl + 'api/Kanban/ImportDeals', form).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro ao importar os cards.');
        return of({ total_linhas: 0, importados: 0, erros: ['Erro ao enviar a planilha para importacao.'] });
      })
    );
  }

  getConfig(): Observable<KanbanConfig> {
    return this.http.post<KanbanConfig>(this.baseUrl + 'api/Kanban/GetConfig', {
      guidclientenapis: this.guid(),
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro ao carregar a configuração do Kanban.');
        return of({ departamentos: [], etapas: [], departamento_sellers: [], sellers: [], formularios: [], formulario_campos: [], formulario_opcoes: [], permitido: false });
      })
    );
  }

  saveDepartment(departamento: any): Observable<number> {
    departamento.guidclientenapis = this.guid();
    departamento.seller_id_usuario = this.sellerId();

    return this.http.post<number>(this.baseUrl + 'api/Kanban/SaveDepartment', {
      departamento: departamento
    }, { headers: this.headers }).pipe(catchError(() => of(0)));
  }

  saveStage(etapa: any): Observable<number> {
    etapa.guidclientenapis = this.guid();
    etapa.seller_id_usuario = this.sellerId();

    return this.http.post<number>(this.baseUrl + 'api/Kanban/SaveStage', {
      etapa: etapa
    }, { headers: this.headers }).pipe(catchError(() => of(0)));
  }

  saveDepartmentSeller(vinculo: any): Observable<boolean> {
    vinculo.guidclientenapis = this.guid();
    vinculo.seller_id_usuario = this.sellerId();

    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/SaveDepartmentSeller', {
      vinculo: vinculo
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  saveFormType(formulario: any): Observable<number> {
    formulario.guidclientenapis = this.guid();
    formulario.seller_id_usuario = this.sellerId();

    return this.http.post<number>(this.baseUrl + 'api/Kanban/SaveFormType', {
      formulario: formulario
    }, { headers: this.headers }).pipe(catchError(() => of(0)));
  }

  saveFormField(campo: any): Observable<number> {
    campo.guidclientenapis = this.guid();
    campo.seller_id_usuario = this.sellerId();

    return this.http.post<number>(this.baseUrl + 'api/Kanban/SaveFormField', {
      campo: campo
    }, { headers: this.headers }).pipe(catchError(() => of(0)));
  }

  saveFormFieldOption(opcao: any): Observable<number> {
    opcao.guidclientenapis = this.guid();
    opcao.seller_id_usuario = this.sellerId();

    return this.http.post<number>(this.baseUrl + 'api/Kanban/SaveFormFieldOption', {
      opcao: opcao
    }, { headers: this.headers }).pipe(catchError(() => of(0)));
  }

  saveDealForm(formulario: any): Observable<boolean> {
    formulario.guidclientenapis = this.guid();
    formulario.seller_id = this.sellerId();

    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/SaveDealForm', {
      formulario: formulario
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  getOpenDealsByClient(clientId: number): Observable<KanbanNegocio[]> {
    return this.http.post<KanbanNegocio[]>(this.baseUrl + 'api/Kanban/GetOpenDealsByClient', {
      guidclientenapis: this.guid(),
      seller_id: this.sellerId(),
      client_id: clientId
    }, { headers: this.headers }).pipe(catchError(() => of([])));
  }

  searchClients(termo: string): Observable<KanbanClienteBusca[]> {
    return this.http.post<KanbanClienteBusca[]>(this.baseUrl + 'api/Kanban/SearchClients', {
      guidclientenapis: this.guid(),
      seller_id: this.sellerId(),
      termo: termo
    }, { headers: this.headers }).pipe(catchError(() => of([])));
  }

  searchProducts(termo: string): Observable<KanbanProdutoBusca[]> {
    return this.http.post<KanbanProdutoBusca[]>(this.baseUrl + 'api/Kanban/SearchProducts', {
      guidclientenapis: this.guid(),
      termo: termo
    }, { headers: this.headers }).pipe(catchError(() => of([])));
  }

  getSellers(departamentoId: number): Observable<KanbanSeller[]> {
    return this.http.post<KanbanSeller[]>(this.baseUrl + 'api/Kanban/GetSellers', {
      guidclientenapis: this.guid(),
      seller_id: this.sellerId(),
      departamento_id: departamentoId
    }, { headers: this.headers }).pipe(catchError(() => of([])));
  }

  getLossReasons(): Observable<KanbanMotivoPerda[]> {
    return this.http.post<KanbanMotivoPerda[]>(this.baseUrl + 'api/Kanban/GetLossReasons', {
      guidclientenapis: this.guid()
    }, { headers: this.headers }).pipe(catchError(() => of([])));
  }

  getDeal(negocioId: number): Observable<KanbanDetalhe> {
    return this.http.post<KanbanDetalhe>(this.baseUrl + 'api/Kanban/GetDeal', {
      guidclientenapis: this.guid(),
      negocio_id: negocioId,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(null)));
  }

  createDeal(deal: any): Observable<number> {
    deal.guidclientenapis = this.guid();
    deal.seller_id_usuario = this.sellerId();

    return this.http.post<number>(this.baseUrl + 'api/Kanban/CreateDeal', {
      deal: deal
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error('', 'Houve um erro ao criar o card.');
        return of(0);
      })
    );
  }

  moveDeal(negocioId: number, etapaId: number): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/MoveDeal', {
      guidclientenapis: this.guid(),
      negocio_id: negocioId,
      etapa_id: etapaId,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  finishDeal(negocioId: number, status: string, motivoPerdaId?: number, observacaoPerda?: string, etapaId?: number): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/FinishDeal', {
      guidclientenapis: this.guid(),
      negocio_id: negocioId,
      status: status,
      motivo_perda_id: motivoPerdaId || null,
      observacao_perda: observacaoPerda || null,
      etapa_id: etapaId || null,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  addNote(negocioId: number, anotacao: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/AddNote', {
      guidclientenapis: this.guid(),
      negocio_id: negocioId,
      anotacao: anotacao,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  updateDealName(negocioId: number, nomeNegocio: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/UpdateDealName', {
      guidclientenapis: this.guid(),
      negocio_id: negocioId,
      nome_negocio: nomeNegocio,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  updateDealInfo(
    negocioId: number,
    valorNegociacao: number,
    dataFechamentoPrevista: string,
    canalOrigem: string,
    sellerResponsavelId: number
  ): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/UpdateDealInfo', {
      guidclientenapis: this.guid(),
      negocio_id: negocioId,
      valor_negociacao: valorNegociacao || null,
      data_fechamento_prevista: dataFechamentoPrevista || null,
      canal_origem: canalOrigem || null,
      seller_id_responsavel: sellerResponsavelId || null,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  updateDealClient(negocioId: number, client: any): Observable<boolean> {
    client.guidclientenapis = this.guid();
    client.negocio_id = negocioId;
    client.seller_id = this.sellerId();

    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/UpdateDealClient', {
      client: client
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  updateNote(anotacaoId: number, anotacao: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/UpdateNote', {
      guidclientenapis: this.guid(),
      anotacao_id: anotacaoId,
      anotacao: anotacao,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  uploadFile(negocioId: number, file: File): Observable<KanbanArquivo> {
    const form = new FormData();
    form.append('guidclientenapis', this.guid());
    form.append('negocio_id', negocioId.toString());
    form.append('seller_id', this.sellerId().toString());
    form.append('arquivo', file, file.name);

    return this.http.post<KanbanArquivo>(this.baseUrl + 'api/Kanban/UploadFile', form).pipe(catchError(() => of(null)));
  }

  openFile(arquivoId: number): Observable<Blob> {
    return this.http.post(this.baseUrl + 'api/Kanban/OpenFile', {
      guidclientenapis: this.guid(),
      arquivo_id: arquivoId
    }, { responseType: 'blob' }).pipe(catchError(() => of(null)));
  }

  deleteFile(arquivoId: number): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/DeleteFile', {
      guidclientenapis: this.guid(),
      arquivo_id: arquivoId,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }

  inactivateDeal(negocioId: number): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'api/Kanban/InactivateDeal', {
      guidclientenapis: this.guid(),
      negocio_id: negocioId,
      seller_id: this.sellerId()
    }, { headers: this.headers }).pipe(catchError(() => of(false)));
  }
}
